import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAdminLoad from "@/hooks/useAdminLoad";
import { useAdmin } from "@/admin/AuthContext";
import { LoadState, PageHeader, Panel } from "@/components/admin/AdminUI";
import ContentEditor from "@/components/admin/ContentEditor";
import TranslateJob from "@/components/admin/TranslateJob";
import TranslationColumn, { needsTranslation } from "@/components/admin/TranslationColumn";
import { CONTENT_DOCS } from "@/lib/constants";

const DOT = { complete: "bg-emerald-500", partial: "bg-amber-400", empty: "bg-red-500" };

function DocList({ docs, items, byDoc, current, onPick }) {
  return (
    <nav aria-label="Documents" className="space-y-1" data-testid="content-doc-list">
      {docs.map((d) => (
        <button key={d.key} onClick={() => onPick(d.key)} aria-current={current === d.key ? "page" : undefined}
          className={`flex h-12 w-full items-center gap-2 rounded-xl px-4 text-left text-sm transition-colors cw-ease ${current === d.key ? "cw-bg-tint cw-brand-deep font-medium" : "hover:bg-[var(--cw-surface-tertiary)]"}`}
          data-testid={`content-doc-${d.key}`}>
          <span className="min-w-0 flex-1 truncate">{d.label}</span>
          <span className="flex items-center gap-1" data-testid={`content-doc-status-${d.key}`}>
            {Object.entries(byDoc[d.key]?.langs || {}).map(([l, s]) => (
              <span key={l} title={`${l.toUpperCase()} ${s.status}${s.draft_needs_review ? " · review" : ""}`} className={`h-2 w-2 rounded-full ${DOT[s.status] || "bg-gray-300"} ${s.draft_needs_review ? "ring-2 ring-amber-300" : ""}`} />
            ))}
          </span>
          <span className="cw-muted text-xs tabular-nums">v{items?.[d.key]?.version ?? byDoc[d.key]?.en_version ?? "—"}</span>
        </button>
      ))}
    </nav>
  );
}

function FilterToggle({ value, onChange, count }) {
  const cls = (on) => `h-9 flex-1 rounded-lg px-2 text-xs font-medium transition-colors cw-ease ${on ? "bg-white shadow-sm" : "cw-soft hover:text-foreground"}`;
  return (
    <div className="mb-2 flex gap-1 rounded-xl bg-[var(--cw-surface-tertiary)] p-1" role="group" aria-label="Filter">
      <button type="button" aria-pressed={value === "all"} onClick={() => onChange("all")} className={cls(value === "all")} data-testid="content-filter-all">All documents</button>
      <button type="button" aria-pressed={value === "needs"} onClick={() => onChange("needs")} className={cls(value === "needs")} data-testid="content-filter-needs">
        <span>Needs translation</span> <span className="tabular-nums">({count})</span>
      </button>
    </div>
  );
}

function EnglishList({ body }) {
  return (
    <Panel className="min-w-0 p-5" data-testid="content-en-list">
      <h2 className="font-semibold">EN · English</h2>
      <p className="cw-soft mt-2 text-sm">English comes from the Prompts and Interests pages.</p>
      <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded-xl bg-[var(--cw-surface-tertiary)] p-3 text-xs" data-no-i18n>{body}</pre>
    </Panel>
  );
}

export default function Content() {
  const [params, setParams] = useSearchParams();
  const isOwner = useAdmin().admin?.role === "owner";
  const [filter, setFilter] = useState("all");
  const en = useAdminLoad("/admin/content");
  const ov = useAdminLoad("/admin/i18n/content");
  const names = useAdminLoad("/admin/i18n/languages");
  const byDoc = useMemo(() => Object.fromEntries((ov.data?.items || []).map((i) => [i.doc, i])), [ov.data]);
  const docs = useMemo(() => [...CONTENT_DOCS, ...(ov.data?.items || []).filter((i) => !CONTENT_DOCS.some((d) => d.key === i.doc))
    .map((i) => ({ key: i.doc, label: i.title || i.doc, list: i.kind === "list", noPreview: i.kind !== "prose" }))], [ov.data]);
  const needs = docs.filter((d) => Object.values(byDoc[d.key]?.langs || {}).some(needsTranslation));
  const doc = docs.some((d) => d.key === params.get("doc")) ? params.get("doc") : "terms";
  const meta = docs.find((d) => d.key === doc);
  const side = useAdminLoad(`/admin/i18n/content/${doc}`);
  const langs = ov.data?.languages || ["fr", "es"];
  const nameOf = (c) => names.data?.items?.find((l) => l.code === c)?.native_name || c.toUpperCase();
  const onSaved = (content) => en.setData((d) => ({ items: { ...d.items, [doc]: content } }));
  const { reload: reloadOv } = ov;
  const { reload: reloadSide } = side;
  const refreshI18n = useCallback(() => { reloadOv(); reloadSide(); }, [reloadOv, reloadSide]);

  return (
    <div data-testid="admin-content-page">
      <PageHeader title="Content" description="Every save creates a new version. The website and app always show the latest one." />
      {isOwner && <div className="mb-6"><TranslateJob onDone={refreshI18n} /></div>}
      <LoadState loading={en.loading} error={en.error} onRetry={en.reload}>
        <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
          <Panel className="h-fit p-2">
            <FilterToggle value={filter} onChange={setFilter} count={needs.length} />
            {filter === "needs" && !needs.length
              ? <p className="cw-muted p-3 text-sm" data-testid="content-filter-empty">Nothing needs translation.</p>
              : <DocList docs={filter === "needs" ? needs : docs} items={en.data?.items} byDoc={byDoc} current={doc} onPick={(k) => setParams({ doc: k })} />}
          </Panel>
          <div className="grid min-w-0 items-start gap-6 2xl:grid-cols-3" data-testid="content-columns">
            {meta?.list ? <EnglishList body={side.data?.en?.body} />
              : en.data && <ContentEditor key={doc} meta={meta} live={en.data.items[doc]} onSaved={(c) => { onSaved(c); refreshI18n(); }} />}
            {langs.map((l) => {
              const s = side.data?.doc === doc ? side.data.langs?.[l] : null;
              return s
                ? <TranslationColumn key={`${doc}-${l}-${s.version ?? 0}`} doc={doc} kind={side.data.kind} legal={side.data.legal} lang={l} name={nameOf(l)} s={s} isOwner={isOwner} onSaved={refreshI18n} />
                : <Panel key={l} className="p-5"><LoadState loading={side.loading} error={side.error} onRetry={side.reload}><span /></LoadState></Panel>;
            })}
          </div>
        </div>
      </LoadState>
    </div>
  );
}
